module Steps.Step3.Types
    ( FillQueueBase
    , FillQueueState
    , FormFillState
    , FormFillRenderOptions
    , FormFillResult(..)
    , DocumentMutationRequest(..)
    , DocumentMutationResult(..)
    , defaultRenderOptions
    , onFirstItem
    , onLastItem
    , currentFillEntry
    , initialLoadOperationKind
    , loadOperationKindForState
    , captureTransitionIntent
    , matchesLookup
    , toggleLock
    , formContext
    , formEditability
    , hasEditableFields
    ) where

import Common

import API.Attachments (Attachment, AttachmentInput)
import API.Comments (Comment, CommentID, CommentInput)
import API.Document (Document, FormReport, ToggleLockResult, applyLockPermission)
import API.Signature (SignOpts, SignResult, SigVerification, SignatureRefresh, SignatureRequest)
import API.Types (DocumentID, ID)
import App.Operation.Types (OperationKind(..))
import App.Route as AppRoute
import Data.Array as Arr
import Steps.Step3.Editability (Step3Editability, step3Editability)
import Steps.Step3.Transition (Step3HistoryMode, TransitionState, enqueueTransition)
import Steps.WorkQueue.Cache (WorkQueueCache, insertDocument)
import Steps.WorkQueue.DocumentIndex (DocumentLookup)
import Steps.WorkQueue.State (FillQueueEntry, FormSectionID, WorkQueueState)
import Steps.WorkQueue.UIState (WorkQueueUiState)
import Steps.WorkQueue.View (WorkQueueResult(..))
import Widgets.Attachments (Attachments)
import Widgets.Comments (Comments)
import Widgets.Form (FormState)
import Widgets.Form.Class (FormContext, FormWidgetAction, initFormContext)
import Widgets.FormPreview (PreviewState)
import Widgets.Signature.Group (SigGroupState)
import Widgets.Signature.Types (FormSignature, SignatureField)

type FormFillRenderOptions =
    { signatures :: Boolean
    , sideAndTopBar :: Boolean
    , updateUrl :: Maybe (Step3HistoryMode -> FillQueueState -> Effect Unit)
    , onCancel :: Maybe (Effect Unit)
    , onFinish :: Maybe (Effect Unit)
    }

defaultRenderOptions :: FormFillRenderOptions
defaultRenderOptions =
    { signatures: true
    , sideAndTopBar: true
    , updateUrl: Nothing
    , onCancel: Nothing
    , onFinish: Nothing
    }

type FillQueueBase r =
    { queue :: Array FillQueueEntry
    , position :: Int
    , anchor :: Maybe FormSectionID
    , activeSection :: Maybe FormSectionID
    , sectionIntent :: AppRoute.SectionIntent
    , transitionState :: TransitionState
    , workQueueState :: WorkQueueState
    , workQueueUi :: WorkQueueUiState
    , workQueueCache :: WorkQueueCache
    | r
    }

type FillQueueState = FillQueueBase
    ( fillState :: FormFillState
    )

type FormFillState =
    { form :: FormState
    , doc :: Document
    , comments :: Comments
    , attachments :: Attachments
    , preview :: PreviewState
    , signatures :: SigGroupState
    }

data FormFillResult
    = JumpIdxF Int (Maybe FormSectionID)
    | JumpF DocumentLookup (Maybe FormSectionID)
    | NextF
    | PrevF
    | CancelF
    | ExportF FormReport
    | SaveCancelF
    | SaveF
    | AutofillF
    | ToggleLockF
    | StateF FormFillState
    | WorkQueueMetadataF WorkQueueState WorkQueueUiState
    | WorkQF WorkQueueResult
    | ActionF FormWidgetAction FormFillState
    | DocumentMutationF FormFillState DocumentMutationRequest
    | ScrollF

data DocumentMutationRequest
    = CreateComment DocumentID CommentInput
    | UpdateComment DocumentID CommentID CommentInput
    | DeleteComment DocumentID CommentID
    | CreateAttachment DocumentID AttachmentInput
    | DeleteAttachment DocumentID ID
    | SignWithCapturedSignature DocumentID SignatureField FormSignature SignOpts
    | SignWithSavedSignature DocumentID SignatureField
    | RefreshSignatureGroup DocumentID
    | RequestSignatures (Array SignatureRequest)

data DocumentMutationResult
    = CommentCreated Comment
    | CommentUpdated Comment
    | CommentDeleted CommentID
    | AttachmentCreated Attachment
    | AttachmentDeleted ID
    | SignatureSigned SignResult
    | SignatureSignedAndRefreshed SignResult SignatureRefresh
    | SignatureGroupRefreshed SignatureRefresh
    | SignaturesRequested (Array SigVerification)

onFirstItem :: FillQueueState -> Boolean
onFirstItem state = state.position <= 0

onLastItem :: FillQueueState -> Boolean
onLastItem state = state.position > length state.queue - 2

currentFillEntry :: forall r. FillQueueBase r -> Maybe FillQueueEntry
currentFillEntry state = Arr.index state.queue state.position

initialLoadOperationKind :: WorkQueueState -> Maybe Document -> OperationKind
initialLoadOperationKind _ maybeDoc = LoadStep3Document (map _.id maybeDoc)

loadOperationKindForState :: forall r. FillQueueBase r -> OperationKind
loadOperationKindForState state = case currentFillEntry state of
    Just { form: Left recentForm } -> LoadStep3Document (Just recentForm.id)
    _ -> LoadStep3Document Nothing

captureTransitionIntent :: FillQueueState -> FormFillResult -> FillQueueState
captureTransitionIntent state = case _ of
    WorkQF (UIStateChange newUi) ->
        state { workQueueUi = newUi }
    WorkQF (JumpToForm navigation _) ->
        enqueueLookupTransition state navigation.lookup navigation.section
    JumpF lookup section ->
        enqueueLookupTransition state lookup section
    JumpIdxF idx section | idx >= 0 && idx < length state.queue ->
        updateTransition state idx section
    NextF | not (onLastItem state) ->
        updateTransition state (state.position + 1) Nothing
    PrevF | state.position > 0 ->
        updateTransition state (state.position - 1) Nothing
    _ ->
        state

enqueueLookupTransition :: FillQueueState -> DocumentLookup -> Maybe FormSectionID -> FillQueueState
enqueueLookupTransition state lookup section = case Arr.findIndex (matchesLookup lookup) state.queue of
    Just idx -> updateTransition state idx section
    Nothing -> state

updateTransition :: FillQueueState -> Int -> Maybe FormSectionID -> FillQueueState
updateTransition state idx section =
    let Tuple _ transitionState = enqueueTransition state.transitionState idx section
    in state { transitionState = transitionState }

matchesLookup :: DocumentLookup -> FillQueueEntry -> Boolean
matchesLookup lookup entry =
    entry.student == lookup.studentId && case entry.form, lookup.documentId of
        Left doc', Just documentId -> doc'.id == documentId
        Left doc', Nothing -> doc'.form_type.id == lookup.formId
        Right _, Just _ -> false
        Right formId, Nothing -> formId == lookup.formId

toggleLock :: ToggleLockResult -> FillQueueState -> FillQueueState
toggleLock result@{ access } state =
    state { fillState = fillState, workQueueState = workQueueState }
  where
    doc = (applyLockPermission result state.fillState.doc) { access = access }
    workQueueState' = insertDocument doc state.workQueueState
    workQueueState = workQueueState'
        { editDocs = map patchEditDoc workQueueState'.editDocs }
    fillState = state.fillState
        { doc = doc
        , form = state.fillState.form { access = access }
        }
    patchEditDoc editDoc
        | editDoc.id == doc.id = applyLockPermission result editDoc
        | otherwise = editDoc

formContext :: WorkQueueCache -> FormFillState -> FormContext
formContext workQueueCache state =
    (initFormContext workQueueCache.account state.doc)
        { access = state.form.access }

formEditability :: FormFillState -> Step3Editability
formEditability state =
    step3Editability state.doc.can.lock state.doc.locked_at state.form

hasEditableFields :: FormFillState -> Boolean
hasEditableFields = _.hasEditableFields <<< formEditability
